import { isRedoEmployeeEmail } from "@redotech/account-common/redo-employee";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import ActivityHeartIcon from "@redotech/redo-icons/activity-heart.svg";
import ActivityHeartFilledIcon from "@redotech/redo-icons/activity-heart_filled.svg";
import AlertCircleIcon from "@redotech/redo-icons/alert-circle.svg";
import AlertCircleFilledIcon from "@redotech/redo-icons/alert-circle_filled.svg";
import BankIcon from "@redotech/redo-icons/bank.svg";
import BankFilledIcon from "@redotech/redo-icons/bank_filled.svg";
import CreditCard01Icon from "@redotech/redo-icons/credit-card-01.svg";
import CreditCard01FilledIcon from "@redotech/redo-icons/credit-card-01_filled.svg";
import CurrencyDollarIcon from "@redotech/redo-icons/currency-dollar-circle.svg";
import CurrencyDollarFilledIcon from "@redotech/redo-icons/currency-dollar-circle_filled.svg";
import DashboardIcon from "@redotech/redo-icons/dashboard.svg";
import DashboardFilledIcon from "@redotech/redo-icons/dashboard_filled.svg";
import DataflowIcon from "@redotech/redo-icons/dataflow-03.svg";
import DataflowFilledIcon from "@redotech/redo-icons/dataflow-03_filled.svg";
import PlayCircleIcon from "@redotech/redo-icons/play-circle.svg";
import ShieldDollarIcon from "@redotech/redo-icons/shield-dollar.svg";
import ShieldDollarFilledIcon from "@redotech/redo-icons/shield-dollar_filled.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useState } from "react";
import { useMatch } from "react-router-dom";
import * as modalStyles from "../../../../payments/chargebacks/disputes.module.css";
import { useDisputeAlertsEnrollmentEnabled } from "../../../../payments/chargebacks/use-dispute-alerts-enrollment-enabled";
import { useDisputeResolveEnabled } from "../../../../payments/chargebacks/use-dispute-resolve-enabled";
import * as styles from "./payments-navigation.module.css";

export function usePaymentsNavigation(): PageNavigationEntry | null {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useContext(TeamContext);

  const disputesEnabled = !!team?.settings.disputes?.enabled;
  const hsaEnabled = !!team?.settings.hsa?.enabled;

  const primaryTab: LinkPrimaryTab = {
    id: "payments",
    hasNotification: false,
    UnfilledIcon: CurrencyDollarIcon,
    FilledIcon: CurrencyDollarFilledIcon,
    type: "link",
    href:
      !disputesEnabled && hsaEnabled
        ? routes.paymentsHsaHref
        : routes.chargebacksHref,
    pathRoot: routes.paymentsBaseUrl,
    friendlyName: t("Payments", {
      context:
        "primary navigation section for merchant money and payment products",
    }),
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "payments",
    name: t("Payments", {
      context:
        "primary navigation section for merchant money and payment products",
    }),
    NavContent: SecondaryPaymentsNavigation,
  };

  return {
    primaryTab,
    secondaryNavigationData,
    isActiveProduct: disputesEnabled || hsaEnabled,
  };
}

import {
  RECLAIM_IMAGE_URL,
  RECLAIM_POSTER_URL,
  RECLAIM_VIDEO_URL,
} from "../../../../payments/chargebacks/reclaim-constants";

const SecondaryPaymentsNavigation = memo(
  function SecondaryPaymentsNavigation() {
    const { t } = useTranslation();
    const routes = useRequiredContext(MerchantRoutesContext);
    const team = useContext(TeamContext);
    const user = useContext(UserContext);
    const [showVideoModal, setShowVideoModal] = useState(false);

    const disputesEnabled = !!team?.settings.disputes?.enabled;
    const hsaEnabled = !!team?.settings.hsa?.enabled;
    // Beta: the questionnaire flow view is Redo-employee-only.
    const showHsaQuestionnaire = hsaEnabled && isRedoEmployeeEmail(user?.email);

    const { id: nodeId, TreeContext } = useNavTreeNode({
      friendlyName: t("Payments", {
        context:
          "primary navigation section for merchant money and payment products",
      }),
    });

    const chargebacksBaseUrl = routes.chargebacksHref;
    const anyChargebacksChildSelected = useMatch(`${chargebacksBaseUrl}/*`);

    return (
      <TreeContext.Provider value={nodeId}>
        {disputesEnabled && <ChargebacksItem />}
        {hsaEnabled && !showHsaQuestionnaire && <PaymentsHsaItem />}
        {showHsaQuestionnaire && <PaymentsHsaFolderItem />}
        {!!anyChargebacksChildSelected && (
          <Flex
            align="center"
            className={styles.videoLauncher}
            cursor="pointer"
            gap="md"
            onClick={() => setShowVideoModal(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setShowVideoModal(true);
              }
            }}
            position="fixed"
            px="lg"
            py="md"
            role="button"
            tabIndex={0}
          >
            <Flex
              align="center"
              borderRadius="sm"
              borderStyle="solid"
              borderWidth="1px"
              className={styles.videoLauncherIcon}
              justify="center"
              shrink={0}
            >
              <PlayCircleIcon height={14} width={14} />
            </Flex>
            <Flex dir="column" gap="xxs">
              <Text
                className={styles.videoLauncherTitle}
                fontSize="xs"
                fontWeight="medium"
              >
                {t("How {{productName}} works", {
                  productName: "Reclaim AI",
                  context:
                    "title for an explanatory video about the named product",
                })}
              </Text>
              <Text className={styles.videoLauncherSubtitle} fontSize="xxs">
                {t("Watch the 2-min video", {
                  context:
                    "link to a short explainer video about Reclaim AI payments",
                })}
              </Text>
            </Flex>
          </Flex>
        )}
        {showVideoModal && (
          <RedoModal
            hideFooter
            hideHeader
            isOpen
            modalSize="md"
            muiClassName={modalStyles.backdropBlur}
            noPadding
            onModalCloseRequested={() => setShowVideoModal(false)}
            title={t("How {{productName}} works", {
              productName: "Reclaim AI",
              context: "title for an explanatory video about the named product",
            })}
          >
            <Flex
              borderTopLeftRadius="lg"
              borderTopRightRadius="lg"
              className={styles.videoHero}
              overflow="hidden"
              position="relative"
            >
              <img
                alt=""
                className={styles.videoHeroImage}
                src={RECLAIM_IMAGE_URL}
              />
              <Flex className={styles.videoHeroOverlay} position="absolute" />
              <Flex
                className={styles.videoHeroText}
                dir="column"
                gap="xs"
                position="absolute"
              >
                <Text
                  fontFamily="serif"
                  fontSize="5xl"
                  fontWeight="regular"
                  textColor="white"
                >
                  {t("How {{productName}} works", {
                    productName: "Reclaim AI",
                    context:
                      "title for an explanatory video about the named product",
                  })}
                </Text>
                <Text className={styles.videoHeroSubtitle} fontSize="sm">
                  {t("Watch the two-minute video", {
                    context:
                      "link to a short explainer video about Reclaim AI payments",
                  })}
                </Text>
              </Flex>
            </Flex>
            <Flex
              bgColor="primary"
              borderRadius="lg"
              className={styles.videoPlayerWrap}
              p="2xl"
              position="relative"
            >
              <video
                className={styles.videoPlayer}
                controls
                playsInline
                poster={RECLAIM_POSTER_URL}
                src={RECLAIM_VIDEO_URL}
              />
            </Flex>
          </RedoModal>
        )}
      </TreeContext.Provider>
    );
  },
);

const ChargebacksItem = memo(function ChargebacksItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { enabled: alertsEnabled } = useDisputeAlertsEnrollmentEnabled();
  const { enabled: resolveEnabled } = useDisputeResolveEnabled();

  const anyChildSelected = useMatch(`${routes.chargebacksHref}/*`);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.chargebacksHref }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={ShieldDollarFilledIcon}
      friendlyName={t("Chargebacks", {
        context: "merchant payments and money operations navigation label",
      })}
      id="chargebacks"
      UnfilledIcon={ShieldDollarIcon}
    >
      {/* Nothing to roll up until the merchant runs more than one product. */}
      {(alertsEnabled || resolveEnabled) && <ChargebacksOverviewItem />}
      {alertsEnabled && <ChargebacksAlertsItem />}
      <ChargebacksReclaimItem />
      {resolveEnabled && <ChargebacksResolveItem />}
    </RedoSecondaryNavItem>
  );
});

const ChargebacksOverviewItem = memo(function ChargebacksOverviewItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.chargebacksOverviewHref }}
      FilledIcon={DashboardFilledIcon}
      friendlyName={t("Overview", {
        context: "payments navigation summary page for chargebacks",
      })}
      id="chargebacks-overview"
      UnfilledIcon={DashboardIcon}
    />
  );
});

const ChargebacksAlertsItem = memo(function ChargebacksAlertsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.chargebacksAlertsHref }}
      FilledIcon={AlertCircleFilledIcon}
      friendlyName={t("Alerts", {
        context: "payments navigation page for chargeback alert notifications",
      })}
      id="chargebacks-alerts"
      UnfilledIcon={AlertCircleIcon}
    />
  );
});

const ChargebacksReclaimItem = memo(function ChargebacksReclaimItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.chargebacksReclaimHref }}
      FilledIcon={CreditCard01FilledIcon}
      friendlyName="Reclaim"
      id="chargebacks-reclaim"
      UnfilledIcon={CreditCard01Icon}
    />
  );
});

const ChargebacksResolveItem = memo(function ChargebacksResolveItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.chargebacksResolveHref }}
      FilledIcon={DataflowFilledIcon}
      friendlyName={t("{{productName}}", {
        productName: "Resolve",
        context:
          "payments navigation label containing the official Resolve product name",
      })}
      id="chargebacks-resolve"
      UnfilledIcon={DataflowIcon}
    />
  );
});

const PaymentsHsaItem = memo(function PaymentsHsaItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.paymentsHsaHref }}
      FilledIcon={ActivityHeartFilledIcon}
      friendlyName={t("{{paymentType}} payments", {
        paymentType: "HSA/FSA",
        context:
          "payments navigation label for the named healthcare payment type",
      })}
      id="payments-hsa"
      UnfilledIcon={ActivityHeartIcon}
    />
  );
});

const PaymentsHsaFolderItem = memo(function PaymentsHsaFolderItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  const anyChildSelected = useMatch(`${routes.paymentsHsaHref}/*`);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.paymentsHsaHref }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={ActivityHeartFilledIcon}
      friendlyName={t("{{paymentType}} payments", {
        paymentType: "HSA/FSA",
        context:
          "payments navigation label for the named healthcare payment type",
      })}
      id="payments-hsa"
      UnfilledIcon={ActivityHeartIcon}
    >
      <PaymentsHsaDashboardItem />
      <PaymentsHsaTransactionsItem />
      <PaymentsHsaPayoutsItem />
      <PaymentsHsaQuestionnaireItem />
    </RedoSecondaryNavItem>
  );
});

const PaymentsHsaDashboardItem = memo(function PaymentsHsaDashboardItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.paymentsHsaHref }}
      FilledIcon={DashboardFilledIcon}
      friendlyName={t("Dashboard", {
        context: "merchant payments and money operations navigation label",
      })}
      id="payments-hsa-dashboard"
      UnfilledIcon={DashboardIcon}
    />
  );
});

const PaymentsHsaTransactionsItem = memo(
  function PaymentsHsaTransactionsItem() {
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.paymentsHsaTransactionsHref }}
        FilledIcon={CreditCard01FilledIcon}
        friendlyName="Transactions"
        id="payments-hsa-transactions"
        UnfilledIcon={CreditCard01Icon}
      />
    );
  },
);

const PaymentsHsaPayoutsItem = memo(function PaymentsHsaPayoutsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.paymentsHsaPayoutsHref }}
      FilledIcon={BankFilledIcon}
      friendlyName="Payouts"
      id="payments-hsa-payouts"
      UnfilledIcon={BankIcon}
    />
  );
});

const PaymentsHsaQuestionnaireItem = memo(
  function PaymentsHsaQuestionnaireItem() {
    const { t } = useTranslation();
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.paymentsHsaQuestionnaireHref }}
        FilledIcon={DataflowFilledIcon}
        friendlyName={t("Questionnaire", {
          context:
            "payments navigation label for the HSA/FSA eligibility questionnaire",
        })}
        id="payments-hsa-questionnaire"
        UnfilledIcon={DataflowIcon}
      />
    );
  },
);
